/**
 * Communication — what the firm sends, when, and what happened. Spec §K.
 * Redrawn in Wave 15E (C14 §10.6; ADR-0033) and split one file per tab:
 * `Templates`, `Notifications`, `Reminders`, `Sent`. This file is the page —
 * the header, the one warning that is true of every tab, the reads, and the
 * tab strip.
 *
 * Four tabs on one route, and the order is the order a firm thinks in:
 *
 *   Templates       what we say                       FR-K2
 *   Notifications   what sends it, and to whom        FR-K3
 *   Reminders       what chases, and when             FR-K4
 *   Sent            what actually went out            FR-K5
 *
 * One route rather than four because they are one decision: a firm switching on
 * a reminder is choosing a template and an audience in the same minute, and
 * three back-buttons between them is how a firm ends up with a rule pointing at
 * a template it has not read.
 *
 * ## The two things this screen must say out loud
 *
 * **Nothing ships switched on** (constitution §5). Every shipped binding and
 * every shipped rule arrives inactive, and three of the bindings are off
 * because the module that publishes the event already sends that message
 * itself — turning one on would send every invitation twice. The `note` on the
 * row says so, on the row, where the switch is.
 *
 * **Two audiences cannot be reached.** `assignee` and `reviewer` are platform
 * user ids and ProDesk holds no address for a member of the firm (spec §9-20,
 * §9-24). The row says so *before* a firm switches it on, not after a delivery
 * row comes back `skipped`.
 *
 * ## The UI gate, and what it is not
 *
 * FiveOX ADR-0029: UI **and** API. `settings:can_update` hides every control
 * that writes, and the service refuses the same calls — a hidden button alone
 * is not a permission check. The resource is `settings` rather than a `comms`
 * of its own because `app/rbac.py` is Wave 1B's file and nothing since may edit
 * it; whether communication deserves its own row is a wave-close finding.
 */

import { Alert, Button, PageHeader, Skeleton, TabPanel, Tabs } from '@fiveox/ui'
import { RefreshCw } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { SETTINGS, useEvaluation } from '../../lib/access'
import { ApiError } from '../../lib/api'
import {
  type BindingRecord,
  type DeliveryRecord,
  type ReminderRecord,
  type RuleRecord,
  type TemplateRecord,
  type Vocabulary,
  comms,
} from '../../lib/api/comms'
import { BindingList } from './Notifications'
import { RuleList } from './Reminders'
import { DeliveryList } from './Sent'
import type { Act } from './shared'
import { TemplateList } from './Templates'

type TabKey = 'templates' | 'bindings' | 'rules' | 'sent'

const TABS: Array<{ key: TabKey; label: string }> = [
  { key: 'templates', label: 'Templates' },
  { key: 'bindings', label: 'Notifications' },
  { key: 'rules', label: 'Reminders' },
  { key: 'sent', label: 'Sent' },
]

export function Comms() {
  const { can } = useEvaluation()
  const mayEdit = can(SETTINGS, 'can_update')

  const [tab, setTab] = useState<TabKey>('templates')
  const [vocab, setVocab] = useState<Vocabulary | null>(null)
  const [templates, setTemplates] = useState<TemplateRecord[]>([])
  const [bindings, setBindings] = useState<BindingRecord[]>([])
  const [rules, setRules] = useState<RuleRecord[]>([])
  const [reminders, setReminders] = useState<ReminderRecord[]>([])
  const [stuck, setStuck] = useState<ReminderRecord[]>([])
  const [deliveries, setDeliveries] = useState<DeliveryRecord[]>([])
  const [error, setError] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    setError(null)
    try {
      // `templates` first and on its own: the first read of it is what installs
      // this firm's shipped set, and the other three would otherwise race it
      // and come back empty on a brand-new firm.
      setTemplates(await comms.templates())
      const [v, b, r, rem, d] = await Promise.all([
        comms.vocabulary(),
        comms.bindings(),
        comms.rules(),
        comms.reminders({ limit: 100 }),
        comms.deliveries({ limit: 100 }),
      ])
      setVocab(v.data)
      setBindings(b)
      setRules(r)
      setReminders(rem.data.reminders)
      setStuck(rem.data.stuck)
      setDeliveries(d.data.deliveries)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Communication settings could not be loaded.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const byId = useMemo(() => Object.fromEntries(templates.map((t) => [t.id, t])), [templates])

  const act: Act = async (run, said) => {
    setError(null)
    setNote(null)
    try {
      await run()
      if (said) setNote(said)
      await load()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work.')
    }
  }

  /** A write that reported its own failure (in a dialog) and succeeded. */
  const saved = async (said: string) => {
    setError(null)
    setNote(said)
    await load()
  }

  const scheduled = reminders.filter((r) => r.status === 'scheduled').length

  return (
    <div className="w-full min-w-0 space-y-6">
      <PageHeader
        title="Communication"
        description="What your firm says to its clients, what sends it, and what happened to it."
        actions={
          mayEdit && tab === 'rules' ? (
            <Button
              variant="outline"
              onClick={() =>
                act(async () => {
                  const { data } = await comms.materialise()
                  setNote(`${data.created} scheduled, ${data.cancelled} no longer needed.`)
                })
              }
            >
              <RefreshCw size={16} aria-hidden /> Refresh what is due
            </Button>
          ) : undefined
        }
      />

      {error && <Alert tone="error">{error}</Alert>}
      {note && <Alert tone="info">{note}</Alert>}

      {/* Said once, at the top, because it is true of every row below and a
          firm that misses it will believe the product is broken rather than
          un-registered. */}
      <Alert tone="warning">
        ProDesk hands every message to the platform’s communication service, which sends it
        under your firm’s own channel when one is connected. The message templates below are{' '}
        <strong>not registered with that service yet</strong>, so sends are recorded and refused
        until they are. Everything you configure here is kept.
      </Alert>

      <div className="min-w-0 space-y-4">
        <Tabs
          idBase="comms"
          tabs={TABS.map((t) => ({
            id: t.key,
            label: t.label,
            badge:
              loading
                ? undefined
                : t.key === 'templates'
                  ? templates.length
                  : t.key === 'bindings'
                    ? bindings.filter((b) => b.active).length || undefined
                    : t.key === 'rules'
                      ? scheduled || undefined
                      : undefined,
          }))}
          active={tab}
          onChange={(id) => setTab(id as TabKey)}
        />
        {loading ? (
          <div role="status" aria-busy aria-label="Loading" className="space-y-3">
            <Skeleton className="h-20 w-full rounded-2xl" />
            <Skeleton className="h-20 w-full rounded-2xl" />
            <Skeleton className="h-20 w-full rounded-2xl" />
          </div>
        ) : (
          <>
            <TabPanel idBase="comms" id="templates" active={tab}>
              <TemplateList templates={templates} mayEdit={mayEdit} act={act} onSaved={saved} />
            </TabPanel>
            <TabPanel idBase="comms" id="bindings" active={tab}>
              <BindingList
                bindings={bindings}
                vocab={vocab}
                mayEdit={mayEdit}
                act={act}
                templateName={(id) => byId[id]?.name ?? '—'}
              />
            </TabPanel>
            <TabPanel idBase="comms" id="rules" active={tab}>
              <RuleList
                rules={rules}
                reminders={reminders}
                stuck={stuck}
                mayEdit={mayEdit}
                act={act}
                onCancelled={saved}
              />
            </TabPanel>
            <TabPanel idBase="comms" id="sent" active={tab}>
              <DeliveryList deliveries={deliveries} />
            </TabPanel>
          </>
        )}
      </div>
    </div>
  )
}
